class ConnectedComponent {
    constructor(box) {

        this.boundingBox = box;

    }

};

class GeometryConnectedComponents {

    constructor(geometry) {

        this.components = [];
        this.vertexComponentId = [];

        if (geometry instanceof THREE.Geometry) {

            this.initGeometry(geometry);

        } else if (geometry instanceof THREE.BufferGeometry) {

            if (geometry.index) {

                this.initIndexedBufferGeometry(geometry);

            } else {

                this.initUnindexedBufferGeometry(geometry);

            }

        } else {

            throw new Error("Invalid geometry given");

        }

    }

    initGeometry(geometry) {

        let [pointPositions, pointByVertex] = this.makePoints(geometry);
        let adjacentPoints = this.emptyAdjacent(geometry, pointPositions.length);
        this.makeAdjacentFromFaces(geometry, adjacentPoints, pointByVertex);
        this.makeComponents(adjacentPoints, pointPositions, pointByVertex);

    }

    initIndexedBufferGeometry(geometry) {

        let [pointPositions, pointByVertex] = this.makePointsBuffer(geometry);
        let adjacentPoints = this.emptyAdjacent(geometry, pointPositions.length);
        this.makeAdjacentFromIndices(geometry, adjacentPoints, pointByVertex);
        this.makeComponents(adjacentPoints, pointPositions, pointByVertex);

    }

    initUnindexedBufferGeometry(geometry) {

        let [pointPositions, pointByVertex] = this.makePointsBuffer(geometry);
        let adjacentPoints = this.emptyAdjacent(geometry, pointPositions.length);
        this.makeAdjacentFromUnindexed(geometry, adjacentPoints, pointByVertex);
        this.makeComponents(adjacentPoints, pointPositions, pointByVertex);

    }

    makePoints(geometry) {

        let pointByPos = new Map();
        let pointCount = 0;
        let pointByVertex = [];
        let pointPositions = [];

        let vertices = geometry.vertices;
        for (let vertexId = 0; vertexId < vertices.length; vertexId++) {

            let pos = vertices[vertexId];
            let posString = [pos.x, pos.y, pos.z].join(' ');

            if (!pointByPos.has(posString)) {

                pointByPos.set(posString, pointCount);
                pointPositions[pointCount] = pos.clone();
                pointCount++;

            }

            pointByVertex[vertexId] = pointByPos.get(posString);

        }

        return [pointPositions, pointByVertex];

    }

    makePointsBuffer(geometry) {

        let pointByPos = new Map();
        let pointCount = 0;
        let pointByVertex = [];
        let pointPositions = [];

        let posAttr = geometry.getAttribute("position");
        for (let vertexId = 0; vertexId < posAttr.count; vertexId++) {

            let pos = posAttr.array.slice(vertexId * 3, vertexId * 3 + 3);
            let posString = pos.join(' ');

            if (!pointByPos.has(posString)) {

                pointByPos.set(posString, pointCount);
                pointPositions[pointCount] = new THREE.Vector3(...pos);
                pointCount++;

            }

            pointByVertex[vertexId] = pointByPos.get(posString);

        }

        return [pointPositions, pointByVertex];

    }

    emptyAdjacent(geometry, pointCount) {

        let adjacentPoints = [];

        for (let pointId = 0; pointId < pointCount; pointId++) {

            adjacentPoints[pointId] = new Set();

        }

        return adjacentPoints;

    }

    makeAdjacentFromFaces(geometry, adjacentPoints, pointByVertex) {

        let faceIndexKeys = ['a', 'b', 'c'];

        let faces = geometry.faces;
        for (let faceId = 0; faceId < faces.length; faceId++) {

            let face = faces[faceId];

            for (let i = 0; i < 3; i++) {

                let index1 = faceIndexKeys[i];
                let index2 = faceIndexKeys[(i + 1) % 3];
                let vertexId1 = face[index1];
                let vertexId2 = face[index2];
                let pointId1 = pointByVertex[vertexId1];
                let pointId2 = pointByVertex[vertexId2];
                adjacentPoints[pointId1].add(pointId2);
                adjacentPoints[pointId2].add(pointId1);

            }

        }

    }

    makeAdjacentFromUnindexed(geometry, adjacentPoints, pointByVertex) {

        let posAttr = geometry.getAttribute("position");
        for (let vertexId = 0; vertexId < posAttr.count; vertexId += 3) {

            for (let i = 0; i < 3; i++) {

                let vertexId1 = vertexId + i;
                let vertexId2 = vertexId + (i + 1) % 3;
                let pointId1 = pointByVertex[vertexId1];
                let pointId2 = pointByVertex[vertexId2];
                adjacentPoints[pointId1].add(pointId2);
                adjacentPoints[pointId2].add(pointId1);

            }

        }

    }

    makeAdjacentFromIndices(geometry, adjacentPoints, pointByVertex) {

        let indAttr = geometry.index;
        for (let indexId = 0; indexId < indAttr.count; indexId += 3) {

            for (let i = 0; i < 3; i++) {

                let vertexId1 = indAttr.array[indexId + i];
                let vertexId2 = indAttr.array[indexId + (i + 1) % 3];
                let pointId1 = pointByVertex[vertexId1];
                let pointId2 = pointByVertex[vertexId2];
                adjacentPoints[pointId1].add(pointId2);
                adjacentPoints[pointId2].add(pointId1);

            }

        }

    }

    makeComponents(adjacentPoints, pointPositions, vertexPoints) {

        let unassignedPoints = new Set();
        for (let pointId = 0; pointId < adjacentPoints.length; pointId++) {

            unassignedPoints.add(pointId);

        }

        let pointComponentId = [];
        let components = [];
        let componentId = 0;
        while (unassignedPoints.size != 0) {

            let pointId = unassignedPoints.values().next().value;
            let componentBox = new THREE.Box3();
            let searchPoints = [pointId];
            unassignedPoints.delete(pointId);

            while (searchPoints.length != 0) {

                let nextSearchPoints = [];

                for (let pointId1 of searchPoints) {

                    componentBox.expandByPoint(pointPositions[pointId1]);
                    pointComponentId[pointId1] = componentId;

                    for (let pointId2 of adjacentPoints[pointId1]) {

                        if (unassignedPoints.has(pointId2)) {

                            nextSearchPoints.push(pointId2);
                            unassignedPoints.delete(pointId2);

                        }

                    }

                }

                searchPoints = nextSearchPoints;

            }

            components[componentId++] = new ConnectedComponent(componentBox);

        }

        let vertexComponentId = [];
        for (let vertexId = 0; vertexId < vertexPoints.length; vertexId++) {

            let pointId = vertexPoints[vertexId];
            vertexComponentId[vertexId] = pointComponentId[pointId];

        }

        this.components = components;
        this.vertexComponentId = vertexComponentId;

    }

    getVertexComponent(vertexId) {

        return this.components[this.vertexComponentId[vertexId]];

    }

};

const ConnectedComponentContext = new class ConnectedComponentContext {

    constructor() {

        this.connectedComponents = new WeakMap();

    }

    getGeometryData(geometry) {

        if (!this.connectedComponents.has(geometry)) {

            let components = new GeometryConnectedComponents(geometry);
            this.connectedComponents.set(geometry, components);

        }

        return this.connectedComponents.get(geometry);

    }

};

export default ConnectedComponentContext;
export { ConnectedComponentContext };
